import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import PageWrapper from 'components/Common/PageWraper';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HubIcon from '@mui/icons-material/Hub';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import SettingsIcon from '@mui/icons-material/Settings';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ReactComponent as UsersIcon } from 'assets/fa-solid_users.svg';
import { ReactComponent as StacksIcon } from 'assets/material-symbols_stacks.svg';
import { ReactComponent as PipelineIcon } from 'assets/eos-icons_pipeline.svg';
import { ReactComponent as NanoTechIcon } from 'assets/hugeicons_nano-technology.svg';
import { ReactComponent as ApiIcon } from 'assets/ant-design_api-filled.svg';
import { ReactComponent as AppsFilledIcon } from 'assets/lsicon_apps-filled.svg';
import { ReactComponent as AlertIcon } from 'assets/boxicons_alert-triangle-filled.svg';
import { ReactComponent as ConditionalIcon } from 'assets/carbon_bpmn-conditional-fill.svg';
import { ReactComponent as CalendarIcon } from 'assets/cuida_calendar-outline.svg';
import { ReactComponent as DatabaseIcon } from 'assets/charm_database.svg';
import { ReactComponent as CloudIcon } from 'assets/akar-icons_cloud.svg';
import { ReactComponent as MetadataIcon } from 'assets/reicon_memo-check.svg';
import { ReactComponent as AppsGridIcon } from 'assets/radix-icons_dashboard.svg';
import { DonutWithLegend, HorizontalBars } from './AboutPepWiseCharts';
import './AboutPepWiseDashboard.scss';

const metrics = [
  { value: '750+', label: 'Registered Users', change: '+67%', icon: <UsersIcon /> },
  { value: '500+', label: 'Applications', change: '+18%', icon: <StacksIcon /> },
  { value: '150K+', label: 'Pipelines Monitored', change: '+25%', icon: <PipelineIcon /> },
  { value: '15+', label: 'Platform Technologies', icon: <NanoTechIcon /> },
  { value: '25+', label: 'Integrations', icon: <ApiIcon /> },
  { value: '9+', label: 'Sectors / Programs', icon: <AppsFilledIcon /> },
  { value: '45K+', label: 'Alerts / week', icon: <AlertIcon /> },
  { value: '450K+', label: 'PRS processed', icon: <ConditionalIcon /> },
];

const coverage = [
  { label: 'MDIP / EDF', value: 28 },
  { label: 'Mosaic', value: 22 },
  { label: 'IBP', value: 18 },
  { label: 'Int Beverages', value: 12 },
  { label: 'NA Beverages', value: 8 },
  { label: 'Others', value: 12 },
].map((d) => ({ ...d, display: `${d.value}%` }));

const personas = [
  { name: 'L1/L2 Operators', value: 28, color: '#716ce7' },
  { name: 'DataOps', value: 18, color: '#51c2af' },
  { name: 'PlatformOps', value: 15, color: '#ff8d8b' },
  { name: 'DPMs', value: 14, color: '#31bfd7' },
  { name: 'Engineering', value: 12, color: '#ffa849' },
  { name: 'Executives', value: 8, color: '#4f7ded' },
  { name: 'Others', value: 5, color: '#69ce8e' },
];

const applications = [
  {
    name: 'Live',
    value: 72,
    color: '#716ce7',
    wall: ['#4b42dc', '#2c20c9', '#4339dc', '#8b87ee', '#b8b5f7', '#7873ea'],
    inner: ['#7a76ea', '#2e22d0', '#2b20bd', '#3a30cf', '#5148e0'],
  },
  {
    name: 'InProgress',
    value: 18,
    color: '#51c2af',
    wall: ['#43a898', '#2f8a7b', '#3d9c8c', '#6fcbb9', '#a5e3d6', '#5bc7b3'],
    inner: ['#9fe0d3', '#62c6b5', '#4fb8a6', '#5fc4b1', '#7fd4c4'],
  },
  {
    name: 'Planned',
    value: 10,
    color: '#ff8d8b',
    wall: ['#ee7f7c', '#d65e5b', '#e46f6c', '#f79d9b', '#fcc3c1', '#f5918f'],
    inner: ['#ffc2c0', '#ff9c9a', '#ff8d8b', '#ffa09e', '#ffb3b1'],
  },
];

const capabilities = [
  { title: 'E2E Pipeline Observability', detail: 'Across Source: Gold', icon: <VisibilityIcon />, color: 'blue' },
  { title: 'Platform Health', detail: 'Databricks, Azure, Storage, Orchestrators', icon: <SettingsIcon />, color: 'navy' },
  { title: 'Data Reconciliation & Quality', detail: 'ICEDQ, DQ rules, Exception Management', icon: <TaskAltIcon />, color: 'green' },
  { title: 'Early Warning Signals', detail: 'Predict potential issues before impact', icon: <ErrorOutlineIcon />, color: 'orange' },
  { title: 'RCA & Impact Intelligence', detail: 'Correlate pipeline, platform, DQ and incidents', icon: <HubIcon />, color: 'purple' },
  { title: 'Automation & Self-Heal', detail: 'Controlled remediation and recovery', icon: <ReplayIcon />, color: 'blue' },
  { title: 'Operational Insights', detail: 'Dashboards for L1/L2/L3, Engineering, DPMs, Executives', icon: <DashboardIcon />, color: 'slate' },
];

const technologies = [
  { label: 'Azure Data Factory', value: 20000 },
  { label: 'Databricks', value: 1200 },
  { label: 'Control-M', value: 9500 },
  { label: 'HWA', value: 6800 },
  { label: 'Informatica', value: 5400 },
  { label: 'SAP (BW/CRM/ECC/IBP)', value: 4800 },
  { label: 'Others', value: 3200 },
].map((d) => ({ ...d, display: d.value.toLocaleString('en-US') }));

const integrations = [
  { title: 'Orchestration & Scheduling', detail: 'ADF | Databricks Workflows | Control-M | HWA | Autosys | SAP', icon: <CalendarIcon /> },
  { title: 'Data Quality & reconciliation', detail: 'ICEDQ | Great Expectations | Informatica CDQ/IICS | Custom Controls', icon: <DatabaseIcon /> },
  { title: 'Platform & Telemetry', detail: 'Azure Monitor | Log Analytics | Event Hub | Kafka | ServiceNow | Databricks (UC/HMS)', icon: <CloudIcon /> },
  { title: 'Metadata & Governance', detail: 'CDC | Metadata Tagging | Lineage', icon: <MetadataIcon /> },
  { title: 'Enterprise Applications', detail: 'SAP | IBP | Mosaic | MDIP | EDF | SIPS | Pepsell | Pepiris | Others', icon: <AppsGridIcon /> },
];

const videoPath = `${process.env.PUBLIC_URL}/videos/about-pepwise`;

const videos = [
  { label: 'What is PepWise?', color: 'green', src: `${videoPath}/what-is-pepwise.mp4` },
  { label: 'How does it work?', color: 'blue', src: `${videoPath}/how-does-it-work.mp4` },
  { label: 'How can it help me?', color: 'orange', src: `${videoPath}/how-can-it-help-me.mp4` },
];


const SectionPanel = ({ title, className = '', children }: {
  title: string;
  className?: string;
  children: React.ReactNode;
}) => (
  <section className={`about-pepwise__panel ${className}`} aria-label={title}>
    <h2 className="about-pepwise__panel-title">{title}</h2>
    {children}
  </section>
);

const AboutPepWiseDashboard = () => {
  const [activeVideo, setActiveVideo] = useState<(typeof videos)[number] | null>(null);
  const [videoFailed, setVideoFailed] = useState(false);

  const openVideo = (video: (typeof videos)[number]) => {
    setVideoFailed(false);
    setActiveVideo(video);
  };

  const closeVideo = () => setActiveVideo(null);

  return (
  <PageWrapper sx={{ p: 0, overflow: 'hidden' }}>
    <main className="about-pepwise">
      <header className="about-pepwise__hero">
        <svg className="about-pepwise__logo" role="img" aria-label="PepWise" width="153" height="35" viewBox="0 0 153 35" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_2107_20190)">
            <path d="M35.9756 10.9287H47.7143C51.7479 10.9287 53.2409 12.7487 53.2409 15.96C53.2409 18.7294 51.7872 20.8731 47.7535 20.8731H41.6638V25.3619H35.9756V10.9287ZM41.6638 17.36H45.3351C46.767 17.36 47.3126 16.9137 47.3126 15.8637C47.3126 14.8137 46.767 14.3281 45.3351 14.3281H41.6638V17.36Z" fill="white"/>
            <path d="M70.4233 22.05C70.0392 23.2006 69.1923 24.15 67.9001 24.7975C66.6079 25.445 64.8749 25.8081 62.5568 25.8081C57.2528 25.8081 54.2275 23.8656 54.2275 19.8231C54.2275 16.0038 57.1306 13.86 62.0941 13.86C67.0576 13.86 70.5674 15.4963 70.4844 21.0175H59.3919C59.3919 21.805 60.2169 23.0169 62.4389 23.0169C63.7486 23.0169 64.5169 22.7325 65.0626 22.0456H70.4277L70.4233 22.05ZM65.4031 18.4713C65.3027 17.2769 64.1546 16.5288 62.4171 16.5288C60.6797 16.5288 59.6538 17.0756 59.3919 18.4713H65.4031Z" fill="white"/>
            <path d="M72.3789 14.3063H77.2595V15.6188H77.2988C78.2068 14.5688 79.5993 14 81.8388 14C85.1042 14 88.112 15.7981 88.112 19.9019C88.112 24.0056 85.1085 25.8038 81.8388 25.8038C79.7827 25.8038 78.329 25.2962 77.4603 24.2462H77.421V29.3212H72.3789V14.3019V14.3063ZM80.2061 22.4131C82.0222 22.4131 82.9476 21.3194 82.9476 19.9062C82.9476 18.4931 82.14 17.3994 80.1843 17.3994C78.2286 17.3994 77.421 18.3269 77.421 19.9062C77.421 21.4856 78.2461 22.4131 80.2061 22.4131Z" fill="white"/>
            <path d="M88.6972 10.9287H94.2021L96.0574 19.74H96.0967L98.2139 10.9287H102.754L104.854 19.74H104.893L106.748 10.9287H112.253L108.036 25.3619H102.448L100.493 17.3556H100.453L98.4758 25.3619H92.8881L88.6929 10.9287H88.6972Z" fill="white"/>
            <path d="M113.445 10.4256H118.487V13.2169H113.445V10.4256ZM113.445 14.3063H118.487V25.3619H113.445V14.3063Z" fill="white"/>
            <path d="M124.236 20.9781C121.311 20.6763 120.464 19.5038 120.464 17.5831C120.464 14.9756 122.883 13.8644 127.807 13.8644C131.234 13.8644 135.006 14.49 135.111 17.6225H129.422C129.422 17.3381 129.322 17.0975 129.038 16.9356C128.754 16.7738 128.292 16.6513 127.545 16.6513C126.03 16.6513 125.751 16.9969 125.751 17.4213C125.751 17.7056 126.013 18.0075 127.104 18.1081L131.906 18.5325C134.63 18.7731 135.656 19.9894 135.656 21.8663C135.656 24.7144 133.194 25.8081 127.873 25.8081C122.551 25.8081 120.351 24.7144 120.189 21.9275H126.118C126.179 22.3738 126.401 22.6538 126.742 22.8156C127.104 22.9775 127.611 23.0169 128.174 23.0169C129.466 23.0169 129.846 22.6713 129.846 22.1681C129.846 21.8444 129.684 21.5425 129.16 21.4813L124.241 20.9738L124.236 20.9781Z" fill="white"/>
            <path d="M152.939 22.05C152.555 23.2006 151.708 24.15 150.416 24.7975C149.124 25.445 147.39 25.8081 145.072 25.8081C139.768 25.8081 136.743 23.8656 136.743 19.8231C136.743 16.0038 139.646 13.86 144.61 13.86C149.573 13.86 153.083 15.4963 153 21.0175H141.907C141.907 21.805 142.733 23.0169 144.955 23.0169C146.264 23.0169 147.033 22.7325 147.578 22.0456H152.943L152.939 22.05ZM147.919 18.4713C147.818 17.2769 146.67 16.5288 144.933 16.5288C143.195 16.5288 142.169 17.0756 141.907 18.4713H147.919Z" fill="white"/>
            <path d="M24.8263 17.2638C25.014 14.6781 24.5687 11.8256 22.4471 10.0975C21.4431 9.3975 20.7664 8.21625 20.8538 6.96938C21.0458 4.27875 23.8266 2.905 26.0661 4.20875C26.5769 4.50188 27.0047 4.935 27.3757 5.41188C30.2395 9.10875 28.5588 14.8006 25.0359 17.3819C24.9442 17.4475 24.8176 17.3819 24.8263 17.2681V17.2638Z" fill="white"/>
            <path d="M27.2362 27.6281C27.5024 27.16 26.9437 26.705 26.4897 26.9894C22.4429 29.54 17.4445 30.5856 12.3937 29.5313C7.73136 28.56 3.81993 25.9613 1.12645 22.4569C0.799042 22.0281 0.113667 22.2644 0.200976 22.7981C1.37528 29.9031 7.63969 35.2188 15.0217 34.9913C20.282 34.8294 24.8046 31.8938 27.2362 27.6281Z" fillRule="evenodd" clipRule="evenodd" fill="white"/>
            <path d="M16.9162 23.9225C16.2003 23.6731 15.6022 23.2794 15.1089 22.7719C15.0565 22.715 15.0303 22.6888 15.0172 22.68C14.9387 22.6275 14.8426 22.6669 14.8208 22.7588C14.8208 22.7719 14.8208 22.8069 14.8208 22.8725V28.0131C15.1788 28.0394 15.5411 28.0481 15.9078 28.0481C19.3871 28.0481 22.6044 26.9281 25.2193 25.0294C26.9262 23.7913 28.3406 22.1331 29.301 20.1731C29.3577 20.0594 29.3184 19.9194 29.2137 19.8494C28.1092 19.1231 26.7865 18.6988 25.3677 18.6988C25.1101 18.6988 24.8569 18.7119 24.6081 18.7381C23.8704 22.155 21.7007 24.2069 18.7497 24.2069C18.0731 24.2069 17.4663 24.1106 16.9249 23.9225H16.9162Z" fill="white"/>
            <path d="M16.5885 20.16C15.1654 20.16 14.4102 18.4538 14.4102 16.3538C14.4102 14.2538 15.161 12.5475 16.5885 12.5475C18.0116 12.5475 18.7669 14.2538 18.7669 16.3538C18.7669 18.4538 18.016 20.16 16.5885 20.16Z" fill="white"/>
            <path d="M4.81497 10.7625C4.75822 10.8806 4.61416 10.9331 4.49193 10.8806C2.48383 9.99688 1.08252 7.98876 1.08252 5.65251C1.08252 4.76438 1.28333 3.92438 1.64566 3.17626C1.70241 3.05813 1.84647 3.00563 1.9687 3.05813C3.97681 3.94188 5.37811 5.95001 5.37811 8.28626C5.37811 9.17438 5.1773 10.0144 4.81497 10.7625Z" fillRule="evenodd" clipRule="evenodd" fill="white"/>
            <path d="M14.7334 10.6746C15.6195 9.41024 16.9205 8.71462 18.6885 8.71462C18.9373 8.71462 19.1948 8.73212 19.448 8.76712C19.4742 7.51587 19.3782 6.28649 19.1643 5.10087C19.0377 4.40087 18.8762 3.71399 18.671 3.04462C18.3698 2.03837 17.9813 1.06712 17.5185 0.139618C17.4618 0.0302427 17.3352 -0.0266323 17.2173 0.00836767C11.2061 1.74087 6.63547 6.97337 5.91081 13.4134C4.61864 12.4771 3.11256 11.7902 1.45369 11.4359C1.06517 11.3527 0.681009 11.2915 0.292485 11.2477C0.139694 11.2302 0.00873088 11.3527 0.00873088 11.5015C0 11.7071 0 11.9084 0 12.1096C0 18.1996 3.40941 23.4934 8.42094 26.1752V9.19587C8.42094 9.12149 8.47769 9.06462 8.5519 9.06462H12.6292C13.642 9.06462 14.4889 9.76024 14.7334 10.6746Z" fill="white"/>
          </g>
          <defs>
            <clipPath id="clip0_2107_20190">
              <rect width="153" height="35" fill="white"/>
            </clipPath>
          </defs>
        </svg>
        <h1>From Observability to<br />Autonomous Operations</h1>
        <p>PepWise connects every data source, orchestrator and application across PepsiCo<br className="about-pepwise__wide-break" /> into one platform for observability and operational intelligence.</p>
        <div className="about-pepwise__media-links" aria-label="PepWise overview topics">
          {videos.map((video) => (
            <button type="button" key={video.label} className={`about-pepwise__media-link about-pepwise__media-link--${video.color}`} onClick={() => openVideo(video)}>
              <PlayCircleOutlineIcon />{video.label}
            </button>
          ))}
        </div>
      </header>

      <div className="about-pepwise__content">
        <section className="about-pepwise__metrics" aria-label="PepWise platform metrics">
          {metrics.map(({ value, label, change, icon }) => (
            <article className="about-pepwise__metric" key={label}>
              <span className="about-pepwise__metric-icon" aria-hidden="true">{icon}</span>
              <div className="about-pepwise__metric-copy">
                <strong>{value}</strong>
                <span>{label}</span>
                {change && <small><ArrowUpwardIcon />{change}<span>vs. Q4 2025</span></small>}
              </div>
            </article>
          ))}
        </section>

        <div className="about-pepwise__overview-grid">
          <SectionPanel title="Top Programs / Sectors by Coverage" className="about-pepwise__coverage">
            <div className="about-pepwise__bars about-pepwise__bars--coverage">
              <HorizontalBars
                data={coverage}
                color="#ffa6a1"
                hoverColor="#ff8d8b"
                axisColor="#a8a8a8"
                rowHeight={31}
                barHeight={16}
                fontSize={11}
                labelFontSize={10}
                labelWidth={36}
                ariaLabel="Top programs and sectors by coverage"
              />
            </div>
          </SectionPanel>

          <SectionPanel title="Users by Persona" className="about-pepwise__personas">
            <div className="about-pepwise__persona-layout">
              <DonutWithLegend
                data={personas}
                centerValue="750+"
                centerLabel="Users"
                centerSize={24}
                className="about-pepwise__donut"
                ariaLabel="750 plus users by persona"
              />
            </div>
          </SectionPanel>

          <SectionPanel title="Applications Onboarded" className="about-pepwise__applications">
            <div className="about-pepwise__applications-layout">
              <DonutWithLegend
                data={applications}
                centerValue="500+"
                centerLabel="Apps"
                centerSize={22}
                threeD
                className="about-pepwise__donut about-pepwise__applications-donut"
                legendClassName="about-pepwise__application-legend"
                ariaLabel="500 plus applications: 72 percent live, 18 percent in progress, 10 percent planned"
              />
            </div>
          </SectionPanel>
        </div>

        <div className="about-pepwise__details-grid">
          <SectionPanel title="What PepWise Delivers" className="about-pepwise__capabilities">
            <p className="about-pepwise__panel-subtitle">Key platform capabilities</p>
            <ul className="about-pepwise__capability-list">
              {capabilities.map(({ title, detail, icon, color }) => (
                <li key={title}>
                  <span className={`about-pepwise__capability-icon about-pepwise__capability-icon--${color}`} aria-hidden="true">{icon}</span>
                  <span><strong>{title}</strong><small>{detail}</small></span>
                </li>
              ))}
            </ul>
          </SectionPanel>

          <SectionPanel title="ADF" className="about-pepwise__adf">
            <span className="about-pepwise__critical">CRITICAL</span>
            <div className="about-pepwise__adf-grid">
              <article><strong>1</strong><span>Max Integration Runtime Available Node Number</span></article>
              <article><strong>3.39 GB</strong><span>Avg &lt; 6GB IR Available Memory</span></article>
              <article><strong>40</strong><span>Avg &gt; 20 IR Queue Length</span></article>
              <article><strong>131</strong><span>Max MVNet IR Pipeline Waiting Queue</span></article>
              <article className="about-pepwise__adf-total"><strong>79</strong><span>Total Pipeline Failed Runs</span></article>
            </div>
          </SectionPanel>

          <SectionPanel title="Top Technologies (Pipelines monitored)" className="about-pepwise__technologies">
            <p className="about-pepwise__panel-subtitle">Pipeline volume by technology</p>
            <div className="about-pepwise__bars about-pepwise__bars--technologies">
              <HorizontalBars
                data={technologies}
                color="#918aea"
                hoverColor="#7a72e0"
                axisColor="#aaa"
                rowHeight={28}
                barHeight={17}
                fontSize={10}
                labelFontSize={9}
                labelWidth={44}
                ariaLabel="Pipelines monitored by technology"
              />
            </div>
          </SectionPanel>

          <SectionPanel title="Data Sources & Integrations" className="about-pepwise__integrations">
            <ul className="about-pepwise__integration-list">
              {integrations.map(({ title, detail, icon }) => (
                <li key={title}>
                  <span aria-hidden="true">{icon}</span>
                  <div><strong>{title}</strong><small>{detail}</small></div>
                </li>
              ))}
            </ul>
          </SectionPanel>
        </div>
      </div>

      <footer className="about-pepwise__footer">
        <div className="about-pepwise__journey" aria-label="See, anticipate, prevent, automate">
          <span>See</span><ArrowForwardIcon /><span>Anticipate</span><ArrowForwardIcon /><span>Prevent</span><ArrowForwardIcon /><span>Automate</span>
        </div>
        <strong>Stronger Data. Smarter Operations. A More Agile PepsiCo.</strong>
      </footer>

      <Dialog open={activeVideo !== null} onClose={closeVideo} PaperProps={{ sx: { '&.MuiDialog-paper': { width: 'min(1080px, 92vw, calc((100vh - 140px) * 16 / 9))', maxWidth: 'none' } } }}>
        <DialogTitle className="about-pepwise__modal-title">
          {activeVideo?.label}
          <IconButton aria-label="Close video" onClick={closeVideo}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {activeVideo && (
            <div className="about-pepwise__video-frame">
              {videoFailed
                ? <p>This video is unavailable right now.</p>
                : <video className="about-pepwise__video" key={activeVideo.src} src={activeVideo.src} controls autoPlay playsInline preload="metadata" onError={() => setVideoFailed(true)} />}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  </PageWrapper>
  );
};

export default AboutPepWiseDashboard;
